import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Slider,
  Switch,
  TextField,
  Button,
  Divider,
  Tooltip,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Shield,
  Lock,
  History,
  AccountCircle,
  ExpandMore,
  ExpandLess,
  Save,
  RestartAlt,
  Info,
  CheckCircle,
  Warning,
  VpnKey,
  Repeat,
  Block,
  Timer,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";
import { setupManagementUrl } from "../../../config";
import { PasswordPolicyQuery } from "./returnfields";

// ─── Defaults ────────────────────────────────────────────────────────────────
const DEFAULT_POLICY = {
  minLength: 12,
  requireLowercase: true,
  requireUppercase: true,
  requireDigit: true,
  requireSpecialChar: true,
  maxPasswordAgeDays: 90,
  minPasswordAgeDays: 1,
  passwordHistoryCount: 10,
  maxConsecutiveRepeats: 3,
  maxFailedAttempts: 5,
  lockoutDurationMinutes: 30,
  failedAttemptWindowMins: 15,
  prohibitUsername: true,
  prohibitFirstName: true,
  prohibitLastName: true,
  prohibitDictionaryWords: true,
  minSubstringLength: 3,
  allowedSpecialChars: "!@#$%^&*()_+-=[]{}|;:,.<>?",
};

// ─── Strength Preview ─────────────────────────────────────────────────────────
const getStrengthScore = (policy) => {
  let score = 0;
  if (policy.minLength >= 12) score++;
  if (policy.minLength >= 15) score++;
  if (policy.requireLowercase) score++;
  if (policy.requireUppercase) score++;
  if (policy.requireDigit) score++;
  if (policy.requireSpecialChar) score++;
  if (policy.maxPasswordAgeDays <= 90) score++;
  if (policy.passwordHistoryCount >= 8) score++;
  if (policy.maxConsecutiveRepeats <= 3) score++;
  if (policy.prohibitDictionaryWords) score++;
  return score;
};

const StrengthBar = ({ score }) => {
  const pct = Math.round((score / 10) * 100);
  const color =
    pct < 40 ? "#ef4444" : pct < 70 ? "#f59e0b" : "#22c55e";
  const label = pct < 40 ? "Weak" : pct < 70 ? "Moderate" : "Strong";
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary", fontFamily: "'DM Mono', monospace" }}>
          Policy Strength
        </Typography>
        <Typography sx={{ fontSize: 11, color, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
          {label} · {pct}%
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "action.hover", overflow: "hidden" }}>
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            bgcolor: color,
            borderRadius: 2,
            transition: "width 0.4s ease, background-color 0.4s ease",
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────
const Section = ({ icon, title, subtitle, children, accent }) => {
  const [open, setOpen] = useState(true);
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
        mb: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: `0 0 0 1px ${accent}33` },
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.8,
          cursor: "pointer",
          bgcolor: `${accent}0d`,
          borderBottom: open ? "1px solid" : "none",
          borderColor: "divider",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            bgcolor: `${accent}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: "text.secondary" }}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ─── Toggle Row ───────────────────────────────────────────────────────────────
const ToggleRow = ({ label, hint, checked, onChange, accent }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1,
      borderBottom: "1px dashed",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: 13 }}>{label}</Typography>
      {hint && (
        <Tooltip title={hint} arrow>
          <Info sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      )}
    </Box>
    <Switch
      size="small"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
          bgcolor: accent,
        },
      }}
    />
  </Box>
);

// ─── Slider Row ───────────────────────────────────────────────────────────────
const SliderRow = ({ label, hint, value, onChange, min, max, step = 1, unit, accent }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13 }}>{label}</Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <Info sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      <Chip
        label={`${value} ${unit}`}
        size="small"
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 11,
          bgcolor: `${accent}18`,
          color: accent,
          fontWeight: 700,
          height: 20,
        }}
      />
    </Box>
    <Slider
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(_, v) => onChange(v)}
      size="small"
      sx={{
        color: accent,
        "& .MuiSlider-thumb": { width: 14, height: 14 },
        "& .MuiSlider-rail": { opacity: 0.2 },
      }}
    />
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography sx={{ fontSize: 10, color: "text.disabled", fontFamily: "'DM Mono', monospace" }}>
        {min} {unit}
      </Typography>
      <Typography sx={{ fontSize: 10, color: "text.disabled", fontFamily: "'DM Mono', monospace" }}>
        {max} {unit}
      </Typography>
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PasswordPolicy = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const base_url = setupManagementUrl.uri;
  const [isEditing, setIsEditing] = useState(true);

  const [policy, setPolicy] = useState({ ...DEFAULT_POLICY });
  const [saved, setSaved] = useState(false);
  const [snack, setSnack] = useState(false);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const result = await graphqlQuery(PasswordPolicyQuery, base_url);

        if (result && result.length > 0) {
          setPolicy(result[0]);
        }
        else {
          setIsEditing(false);
        }
      } catch (err) {
        console.error("Failed to fetch password policy:", err);
      }
    };

    fetchPolicy();
  }, []);


  const set = (key) => (val) => setPolicy((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    const mutationData = {
      policyName: policy.policyName ?? "Password Policy",
      minLength: policy.minLength,
      requireLowercase: policy.requireLowercase ? 1 : 0,
      requireUppercase: policy.requireUppercase ? 1 : 0,
      requireDigit: policy.requireDigit ? 1 : 0,
      requireSpecialChar: policy.requireSpecialChar ? 1 : 0,
      allowedSpecialChars: policy.allowedSpecialChars,
      maxPasswordAgeDays: policy.maxPasswordAgeDays,
      minPasswordAgeDays: policy.minPasswordAgeDays,
      passwordHistoryCount: policy.passwordHistoryCount,
      maxConsecutiveRepeats: policy.maxConsecutiveRepeats,
      failedAttemptWindowMins: policy.failedAttemptWindowMins,
      lockoutDurationMinutes: policy.lockoutDurationMinutes,
      maxFailedAttempts: policy.maxFailedAttempts,
      prohibitUsername: policy.prohibitUsername ? 1 : 0,
      prohibitFirstName: policy.prohibitFirstName ? 1 : 0,
      prohibitLastName: policy.prohibitLastName ? 1 : 0,
      prohibitDictionaryWords: policy.prohibitDictionaryWords ? 1 : 0,
      minSubstringLength: policy.minSubstringLength,
      isActive: 1
    };
    const finaldata = isEditing ? { id: policy.id, ...mutationData } : mutationData;

    const response = await generateAndExecuteMutation(
      isEditing ? "updatePasswordPolicy" : "createPasswordPolicy",
      finaldata,
      "passwordPolicy",
      base_url
    );

    // hook into your mutation here
    setSaved(true);
    setSnack(true);
  };

  const handleReset = () => {
    setPolicy({ ...DEFAULT_POLICY });
    setSaved(false);
  };

  const strength = getStrengthScore(policy);

  // accent palette per section
  const A = {
    length: isDark ? "#60a5fa" : "#2563eb",
    complex: isDark ? "#a78bfa" : "#7c3aed",
    age: isDark ? "#34d399" : "#059669",
    history: isDark ? "#fb923c" : "#ea580c",
    lockout: isDark ? "#f87171" : "#dc2626",
    name: isDark ? "#e879f9" : "#a21caf",
  };

  return (
    <>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');
      `}</style>

      <Box
        sx={{
          maxWidth: 780,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 3,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* ── Page Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Shield sx={{ color: A.lockout, fontSize: 22 }} />
              <Typography
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 800,
                  fontSize: { xs: 20, sm: 24 },
                  letterSpacing: "-0.02em",
                }}
              >
                Password Policy
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Configure security rules applied to all users in your organisation
            </Typography>
          </Box>

          {/* Strength badge */}
          <Box
            sx={{
              minWidth: 200,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <StrengthBar score={strength} />
          </Box>
        </Box>

        {/* ── 1. Minimum Length ── */}
        <Section
          icon={<VpnKey fontSize="small" />}
          title="Minimum Length"
          subtitle="Minimum number of characters required"
          accent={A.length}
        >
          <SliderRow
            label="Minimum Characters"
            hint="NIST recommends at least 12 characters. 15+ is ideal for high-security environments."
            value={policy.minLength}
            onChange={set("minLength")}
            min={6}
            max={32}
            unit="chars"
            accent={A.length}
          />
        </Section>

        {/* ── 2. Complexity ── */}
        <Section
          icon={<Lock fontSize="small" />}
          title="Complexity Requirements"
          subtitle="Character type rules that passwords must satisfy"
          accent={A.complex}
        >
          <ToggleRow
            label="Require Lowercase Letter"
            hint="At least one a–z character"
            checked={policy.requireLowercase}
            onChange={set("requireLowercase")}
            accent={A.complex}
          />
          <ToggleRow
            label="Require Uppercase Letter"
            hint="At least one A–Z character"
            checked={policy.requireUppercase}
            onChange={set("requireUppercase")}
            accent={A.complex}
          />
          <ToggleRow
            label="Require Digit"
            hint="At least one 0–9 character"
            checked={policy.requireDigit}
            onChange={set("requireDigit")}
            accent={A.complex}
          />
          <ToggleRow
            label="Require Special Character"
            hint="At least one character such as !@#$%^&*()"
            checked={policy.requireSpecialChar}
            onChange={set("requireSpecialChar")}
            accent={A.complex}
          />

          <Box sx={{ mt: 2 }}>
            <SliderRow
              label="Max Consecutive Repeating Characters"
              hint='Prevents patterns like "aaa" or "111". Set to 3 to block 3 or more identical consecutive chars.'
              value={policy.maxConsecutiveRepeats}
              onChange={set("maxConsecutiveRepeats")}
              min={2}
              max={10}
              unit="chars"
              accent={A.complex}
            />
          </Box>
        </Section>

        {/* ── 3. Password Age ── */}
        <Section
          icon={<Timer fontSize="small" />}
          title="Password Age"
          subtitle="Control how long a password remains valid and how soon it can be changed"
          accent={A.age}
        >
          <SliderRow
            label="Maximum Password Age"
            hint="Users must change their password after this many days. 90 days is the common enterprise standard."
            value={policy.maxPasswordAgeDays}
            onChange={set("maxPasswordAgeDays")}
            min={7}
            max={365}
            step={7}
            unit="days"
            accent={A.age}
          />
          <SliderRow
            label="Minimum Password Age"
            hint="Prevents users from immediately cycling back to a previous password."
            value={policy.minPasswordAgeDays}
            onChange={set("minPasswordAgeDays")}
            min={0}
            max={30}
            unit="days"
            accent={A.age}
          />
        </Section>

        {/* ── 4. Password History ── */}
        <Section
          icon={<History fontSize="small" />}
          title="Password History"
          subtitle="Enforce uniqueness by preventing reuse of recent passwords"
          accent={A.history}
        >
          <SliderRow
            label="Remember Last N Passwords"
            hint="Users cannot reuse any of their last N passwords. 10 is a common baseline."
            value={policy.passwordHistoryCount}
            onChange={set("passwordHistoryCount")}
            min={1}
            max={24}
            unit="passwords"
            accent={A.history}
          />
        </Section>

        {/* ── 5. Account Lockout ── */}
        <Section
          icon={<Block fontSize="small" />}
          title="Account Lockout"
          subtitle="Limit failed login attempts to prevent brute-force attacks"
          accent={A.lockout}
        >
          <SliderRow
            label="Max Failed Attempts"
            hint="Number of consecutive failed logins before the account is locked."
            value={policy.maxFailedAttempts}
            onChange={set("maxFailedAttempts")}
            min={1}
            max={20}
            unit="attempts"
            accent={A.lockout}
          />
          <SliderRow
            label="Lockout Duration"
            hint="How long the account remains locked before auto-unlocking or requiring admin action."
            value={policy.lockoutDurationMinutes}
            onChange={set("lockoutDurationMinutes")}
            min={1}
            max={1440}
            step={5}
            unit="mins"
            accent={A.lockout}
          />
          <SliderRow
            label="Failed Attempt Window"
            hint="The rolling time window within which failed attempts are counted."
            value={policy.failedAttemptWindowMins}
            onChange={set("failedAttemptWindowMins")}
            min={1}
            max={60}
            unit="mins"
            accent={A.lockout}
          />
        </Section>

        {/* ── 6. Name & Dictionary ── */}
        <Section
          icon={<AccountCircle fontSize="small" />}
          title="Name & Dictionary Restrictions"
          subtitle="Prevent use of personal information or common words in passwords"
          accent={A.name}
        >
          <ToggleRow
            label="Prohibit Username"
            hint="Blocks passwords that contain the user's login username"
            checked={policy.prohibitUsername}
            onChange={set("prohibitUsername")}
            accent={A.name}
          />
          <ToggleRow
            label="Prohibit First Name"
            hint="Blocks passwords that contain the user's first name"
            checked={policy.prohibitFirstName}
            onChange={set("prohibitFirstName")}
            accent={A.name}
          />
          <ToggleRow
            label="Prohibit Last Name"
            hint="Blocks passwords that contain the user's last name"
            checked={policy.prohibitLastName}
            onChange={set("prohibitLastName")}
            accent={A.name}
          />
          <ToggleRow
            label="Prohibit Dictionary Words"
            hint="Blocks passwords that match entries in the global or company prohibited words list"
            checked={policy.prohibitDictionaryWords}
            onChange={set("prohibitDictionaryWords")}
            accent={A.name}
          />

          <Box sx={{ mt: 2 }}>
            <SliderRow
              label="Minimum Substring Match Length"
              hint="How many consecutive characters of a name must appear in the password to trigger a block. e.g. 3 would block 'joh' from 'John'."
              value={policy.minSubstringLength}
              onChange={set("minSubstringLength")}
              min={2}
              max={10}
              unit="chars"
              accent={A.name}
            />
          </Box>
        </Section>

        {/* ── Policy Summary ── */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            p: 2.5,
            mb: 3,
            bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              mb: 1.5,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Policy Summary
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
              gap: 1,
            }}
          >
            {[
              { label: "Min Length", value: `${policy.minLength} chars` },
              { label: "Max Age", value: `${policy.maxPasswordAgeDays} days` },
              { label: "Min Age", value: `${policy.minPasswordAgeDays} days` },
              { label: "History", value: `Last ${policy.passwordHistoryCount}` },
              { label: "Max Repeats", value: `${policy.maxConsecutiveRepeats} chars` },
              { label: "Max Attempts", value: `${policy.maxFailedAttempts} tries` },
              { label: "Lockout", value: `${policy.lockoutDurationMinutes} mins` },
              { label: "Attempt Window", value: `${policy.failedAttemptWindowMins} mins` },
              { label: "Substring ≥", value: `${policy.minSubstringLength} chars` },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: 10, color: "text.disabled", fontFamily: "'DM Mono', monospace", textTransform: "uppercase" }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 13, fontFamily: "'DM Mono', monospace", fontWeight: 500 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Complexity flags */}
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.5 }}>
            {[
              { key: "requireLowercase", label: "a–z" },
              { key: "requireUppercase", label: "A–Z" },
              { key: "requireDigit", label: "0–9" },
              { key: "requireSpecialChar", label: "!@#" },
              { key: "prohibitUsername", label: "No Username" },
              { key: "prohibitFirstName", label: "No First Name" },
              { key: "prohibitLastName", label: "No Last Name" },
              { key: "prohibitDictionaryWords", label: "No Dictionary" },
            ].map(({ key, label }) => (
              <Chip
                key={key}
                label={label}
                size="small"
                icon={policy[key] ? <CheckCircle sx={{ fontSize: "12px !important" }} /> : <Warning sx={{ fontSize: "12px !important" }} />}
                sx={{
                  fontSize: 10,
                  height: 22,
                  bgcolor: policy[key]
                    ? isDark ? "rgba(34,197,94,0.15)" : "rgba(5,150,105,0.1)"
                    : isDark ? "rgba(239,68,68,0.12)" : "rgba(220,38,38,0.08)",
                  color: policy[key]
                    ? isDark ? "#4ade80" : "#059669"
                    : isDark ? "#f87171" : "#dc2626",
                  border: "1px solid",
                  borderColor: policy[key]
                    ? isDark ? "rgba(34,197,94,0.3)" : "rgba(5,150,105,0.25)"
                    : isDark ? "rgba(239,68,68,0.25)" : "rgba(220,38,38,0.2)",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* ── Actions ── */}
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "'DM Sans', sans-serif",
              borderRadius: 2,
              fontSize: 13,
            }}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "'DM Sans', sans-serif",
              borderRadius: 2,
              fontSize: 13,
              bgcolor: A.length,
              "&:hover": { bgcolor: A.length, filter: "brightness(1.1)" },
              boxShadow: `0 0 0 0 ${A.length}`,
            }}
          >
            Save Policy
          </Button>
        </Box>
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack}
        autoHideDuration={3500}
        onClose={() => setSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnack(false)}
          sx={{ fontFamily: "'DM Sans', sans-serif", borderRadius: 2 }}
        >
          Password policy saved successfully.
        </Alert>
      </Snackbar>
    </>
  );
};

export default PasswordPolicy;